import { createContext, useState, useContext, useEffect } from "react";
import { getCostCentres } from "../apis/kilnBackendApis";
import { mergeWithFallback } from "../utils";
import { tempMissingCostCentres, tempCostCentreDisplayOverrides } from "../constants/tempMissingApiData";
import PropTypes from "prop-types";

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const [allCostCentres, setAllCostCentres] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Function to load ALL cost centres once when the provider mounts
  const loadAllCostCentres = async () => {
    // In test environment, provide mock data instead of making API calls
    if (import.meta.env.MODE === "test" || import.meta.env.VITEST) {
      const mockData = [
        { costCentre: "CC_01 - Engineering (1000-100-001)", function: "Engineering" },
        { costCentre: "CC_02 - Sales (2000-200-002)", function: "Sales" },
        { costCentre: "CC_03 - Marketing (3000-300-003)", function: "Marketing" },
        { costCentre: "CC_04 - HR (4000-400-004)", function: "HR" },
      ];
      setAllCostCentres(mockData);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Load ALL cost centres without any function filter
      const response = await getCostCentres();
      const apiCostCentres = response.data || [];

      // Merge API cost centres with temporary missing entries, deduping by code
      const mergedCostCentres = mergeWithFallback(
        apiCostCentres,
        tempMissingCostCentres,
        (costCentre) => costCentre.code,
      );

      // Apply display overrides to all cost centres after merging
      const costCentresWithOverrides = mergedCostCentres.map((costCentre) => ({
        ...costCentre,
        display: tempCostCentreDisplayOverrides[costCentre.code] ?? costCentre.display,
      }));

      setAllCostCentres(costCentresWithOverrides);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error loading cost centres:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load all cost centres once when the provider mounts
  useEffect(() => {
    loadAllCostCentres();
  }, []);

  // Function to get cost centres filtered by function (client-side filtering)
  const getCostCentresByFunction = (functionName) => {
    if (!functionName) {
      // If no function selected, return empty array to disable the field
      return [];
    }
    // Filter the already loaded cost centres by function
    return allCostCentres.filter((costCentre) => costCentre.function === functionName);
  };

  const value = {
    costCentres: allCostCentres, // Keep backward compatibility
    allCostCentres,
    getCostCentresByFunction,
    isLoading,
    error,
    // Helper to check if data is loaded
    isLoaded: allCostCentres.length > 0 && !isLoading,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

OrganizationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
